/*
 * Where a signed-in reader's My Study lives in Firestore:
 *
 *   users/{uid}/study/{item}      one document per saved person, page or
 *                                 passage, with its note -- the same entry
 *                                 shape localStorage keeps (see myStudy.js)
 *   users/{uid}/progress/paths    { paths: { [pathId]: { visited, updatedAt } } }
 *
 * One document per item, not one list, so saving a note writes one small
 * document instead of the whole study, two devices editing different notes
 * cannot overwrite each other, and no reader nears the 1 MB document limit.
 *
 * Item keys contain "/" (page:/isaiah), which Firestore reads as a path
 * separator, so document ids are the key URI-encoded; the key itself is kept
 * inside the document.
 */

/* Firestore allows 500 writes in one batch. */
const BATCH_LIMIT = 450;

export function studyDocId(key) {
  return encodeURIComponent(key);
}

/* Firestore rejects undefined fields; JSON drops them. */
function plain(entry) {
  return JSON.parse(JSON.stringify(entry));
}

export function connectStudyCloud({ db, firestoreApi, uid }) {
  const {
    collection,
    deleteDoc,
    doc,
    getDoc,
    getDocs,
    onSnapshot,
    setDoc,
    writeBatch,
  } = firestoreApi;

  const itemsRef = collection(db, "users", uid, "study");
  const progressRef = doc(db, "users", uid, "progress", "paths");
  const itemRef = (key) => doc(itemsRef, studyDocId(key));

  async function commitInChunks(writes) {
    for (let start = 0; start < writes.length; start += BATCH_LIMIT) {
      const batch = writeBatch(db);
      writes.slice(start, start + BATCH_LIMIT).forEach((write) => write(batch));
      // eslint-disable-next-line no-await-in-loop
      await batch.commit();
    }
  }

  const adapter = {
    putItem: (entry) => setDoc(itemRef(entry.key), plain(entry)),
    deleteItem: (key) => deleteDoc(itemRef(key)),
    putPathProgress: (progress) =>
      setDoc(progressRef, { paths: plain(progress), updatedAt: Date.now() }),
    async clearAll() {
      const snapshot = await getDocs(itemsRef);
      await commitInChunks([
        ...snapshot.docs.map((item) => (batch) => batch.delete(item.ref)),
        (batch) => batch.delete(progressRef),
      ]);
    },
  };

  return {
    adapter,

    /** The account's study as it stands, once -- for merging on sign-in. */
    async fetchAll() {
      const [items, progress] = await Promise.all([
        getDocs(itemsRef),
        getDoc(progressRef),
      ]);
      return {
        items: items.docs.map((item) => item.data()),
        pathProgress: progress.exists() ? progress.data().paths || {} : {},
      };
    },

    /** Writes several items and the path progress together. */
    writeMany(items, pathProgress) {
      const writes = items.map((entry) => (batch) =>
        batch.set(itemRef(entry.key), plain(entry))
      );
      if (pathProgress) {
        writes.push((batch) =>
          batch.set(progressRef, {
            paths: plain(pathProgress),
            updatedAt: Date.now(),
          })
        );
      }
      return commitInChunks(writes);
    },

    /**
     * Live updates, including this device's own writes before the server has
     * confirmed them and other devices' writes as they land. Returns the
     * unsubscribe function.
     */
    subscribe(onChange, onError) {
      const stopItems = onSnapshot(
        itemsRef,
        (snapshot) => onChange({ items: snapshot.docs.map((item) => item.data()) }),
        onError
      );
      const stopProgress = onSnapshot(
        progressRef,
        (snapshot) =>
          onChange({
            pathProgress: snapshot.exists() ? snapshot.data().paths || {} : {},
          }),
        onError
      );
      return () => {
        stopItems();
        stopProgress();
      };
    },
  };
}
